<template>
    <div class="content-container">
        <div class="filter-container">
            <Filter
                :filter="filter"
                :statusList="statusList"
                class="filter"
                @addFilterStatus="addFilterStatus"
                @clearFilter="clearFilter"
                @removeFilterStatus="removeFilterStatus"
                @search="search"
                @setInterventionId="setInterventionId"
                @setPredictedDateFrom="setPredictedDateFrom"
                @setPredictedDateTo="setPredictedDateTo"
                @statusToggleSelectAll="statusToggleSelectAll"
            />
        </div>
        <div class="dataTable">
            <div style="margin-bottom: 8px">
                <h2>{{ $tc('preventive.list.title', +count) }}</h2>
                <BorderedIconButton
                    v-if="!isExportDisabled"
                    :imgUrl="iconeXls"
                    :title="$t('list.export')"
                    @click="exportXls"
                />
            </div>
            <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
            <div v-else ref="content" class="datatable-container-smaller" @scroll="handleScroll">
                <DataTable
                    :count="+count"
                    :isLoading="isLoading"
                    :stop-propa-on-pdf="true"
                    :tableData="tableData"
                    :tableHeader="tableHeader"
                    @downloadPdf="downloadPdf"
                    @select="goToPage"
                    @sort="doSort"
                />
            </div>
        </div>
        <WaitingModal v-if="isExportXlsLoading" />
    </div>
</template>
<script lang="ts">
import { defineComponent, PropType, ref } from 'vue';
import { EQUIPEMENT } from '@/modules/Equipements/store/mutation-types';

import { store } from '@/plugins/store';
import { SortType, sortValue } from '@/models/SortType';
import router from '@/router';
import DownloadPdf from '@/common/utils/DownloadPdf';
import { EtatsTravaux } from '@/models/InterventionNonPreventive';
import iconeXls from '@/assets/img/xls.svg';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import { InterventionPreventive } from '@/models/InterventionPreventive';
import { SuiviInterventionPreventiveActions } from '@/modules/SuiviInterventionsPreventives/store/action-types';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import DataTable from '@/components/ui-components/DataTable.vue';
import Filter from '@/modules/Common/components/Filter.vue';
import {
    FilterInterventionsPreventives
} from '@/modules/SuiviInterventionsPreventives/services/SuiviInterventionPreventiveService';
import { EquipementItems } from '@/models/EquipementItems';
import StringUtils from '@/common/utils/StringUtils';

export default defineComponent({
    name: 'InterventionsPreventivesTab',
    components: { Filter, DataTable, LoaderComponent, BorderedIconButton, WaitingModal },

    props: {
        phaseId: {
            type: Number,
            required: true,
        },
        equipement: {
            type: Object as PropType<EquipementItems>,
            required: true,
        },
    },
    data() {
        return {
            filter: {
                statusList: [] as string[],
                interventionId: '',
                predictedDateFrom: '',
                predictedDateTo: '',
            },
        };
    },
    setup() {
        const content = ref<HTMLElement | null>(null);
        return {
            content,
            iconeXls,
        };
    },
    beforeMount() {
        this.filter.statusList = [...this.filterStatus];
        this.filter.interventionId = this.filterInterventionId;
        this.filter.predictedDateFrom = this.filterPredictedDateFrom;
        this.filter.predictedDateTo = this.filterPredictedDateTo;
    },
    computed: {
        interventionsPreventivesState() {
            return store.state.equipementState.interventionsPreventives;
        },
        tableData() {
            return store.getters['equipementState/getInterventionsPreventives'].map(
                ({
                    id,
                    typeIntervention,
                    periodicite,
                    dateDebutPrevue,
                    dateFinReelle,
                    etat,
                    isRapportPresent,
                }: InterventionPreventive) => {
                    etat = statusName(etat);
                    const periodiciteAsString = StringUtils.getPeriodiciteAsString(periodicite);

                    return {
                        id: { pageName: 'interventionPreventiveDetailView', value: id, label: id },
                        typeIntervention: typeIntervention,
                        periodicite: periodiciteAsString,
                        dateDebutPrevue: dateDebutPrevue ? new Date(dateDebutPrevue).toLocaleDateString() : '',
                        dateFinReelle: dateFinReelle ? new Date(dateFinReelle).toLocaleDateString() : '',
                        statut: etat,
                        pdfExist: isRapportPresent,
                    };
                }
            );
        },
        tableHeader() {
            return store.getters['equipementState/getInterventionsPreventivesHeaders'];
        },
        count() {
            return store.getters['equipementState/getInterventionsPreventivesCount'];
        },
        isLoading() {
            return store.getters['equipementState/getInterventionsPreventivesLoading'];
        },
        page() {
            return store.getters['equipementState/getInterventionsPreventivesPage'];
        },
        numberOfPages() {
            return store.getters['equipementState/getInterventionsPreventivesNbOfPages'];
        },
        filterStatus(): string[] {
            return store.getters['equipementState/getInterventionsPreventivesFilter'].statusList;
        },
        filterInterventionId(): string {
            return store.getters['equipementState/getInterventionsPreventivesFilter'].interventionId;
        },
        filterPredictedDateFrom(): string {
            return store.getters['equipementState/getInterventionsPreventivesFilter'].predictedDateFrom;
        },
        filterPredictedDateTo(): string {
            return store.getters['equipementState/getInterventionsPreventivesFilter'].predictedDateTo;
        },
        statusList() {
            return Object.values(EtatsTravaux);
        },
        allStatusSelected() {
            return this.filter.statusList.length === this.statusList.length || this.filter.statusList.length === 0;
        },
        isExportDisabled() {
            return this.tableData.length === 0 || this.isLoading;
        },
        isExportXlsLoading() {
            return store.getters['equipementState/getInterventionsPreventivesExportXlsIsLoading'];
        },
    },
    mounted() {
        this.getInterventionPreventivesData();
    },
    methods: {
        getInterventionPreventivesData(page = 0) {
            const storeFilter = store.getters['equipementState/getInterventionsPreventivesFilter'];
            const filters: FilterInterventionsPreventives = {
                page,
                id: storeFilter.interventionId !== '' ? storeFilter.interventionId : undefined,
                etats: this.allStatusSelected ? [] : storeFilter.statusList,
                phaseIds: [this.phaseId],
                dateDebutPeriodeRechercheParDate:
                    storeFilter.predictedDateFrom !== '' ? storeFilter.predictedDateFrom : undefined,
                dateFinPeriodeRechercheParDate:
                    storeFilter.predictedDateTo !== '' ? storeFilter.predictedDateTo : undefined,
                sort: this.interventionsPreventivesState.sort
                    ? sortValue(
                          this.interventionsPreventivesState.sort.sortData,
                          this.interventionsPreventivesState.sort.sortStatus
                      )
                    : undefined,
                isBim: true,
            };

            store.dispatch(
                `suiviInterventionPreventiveModuleState/${SuiviInterventionPreventiveActions.GET_SUIVIS_INTERVENTION}`,
                filters,
                {
                    root: true,
                }
            );
        },
        search() {
            store.commit(
                `equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_FILTER_INTERVENTION_ID}`,
                this.filter.interventionId
            );
            store.commit(
                `equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_FILTER_STATUS}`,
                this.filter.statusList
            );
            store.commit(
                `equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_FILTER_PREDICTED_DATE_FROM}`,
                this.filter.predictedDateFrom
            );
            store.commit(
                `equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_FILTER_PREDICTED_DATE_TO}`,
                this.filter.predictedDateTo
            );

            store.commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_ITEMS}`, []);
            this.getInterventionPreventivesData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            store.commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_SORT}`, sort);
            store.commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_ITEMS}`, []);
            this.getInterventionPreventivesData();
        },
        setInterventionId(value: string) {
            this.filter.interventionId = value;
        },
        setPredictedDateFrom(value: string) {
            this.filter.predictedDateFrom = value;
        },
        setPredictedDateTo(value: string) {
            this.filter.predictedDateTo = value;
        },
        addFilterStatus(status: string) {
            this.filter.statusList.push(status);
        },
        removeFilterStatus(status: string) {
            this.filter.statusList = this.filter.statusList.filter(s => s !== status);
        },
        statusToggleSelectAll() {
            this.filter.statusList =
                this.filter.statusList.length === this.statusList.length ? [] : [...this.statusList];
        },
        clearFilter() {
            store.commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_RESET_FILTER}`);
            this.filter.interventionId = '';
            this.filter.predictedDateFrom = '';
            this.filter.predictedDateTo = '';
            this.filter.statusList = [...this.statusList];

            this.search();
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.getInterventionPreventivesData(this.page + 1);
            }
        },
        goToPage(item: { id: { pageName: string; value: number } }) {
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
        downloadPdf(item: { id: { pageName: string; value: number } }) {
            DownloadPdf.getInterventionPdf(item.id.value, 'Rapport_intervention-');
        },
        exportXls() {
            store.dispatch(
                `suiviInterventionPreventiveModuleState/${SuiviInterventionPreventiveActions.EXPORT_XLS_INTERVENTION_PREVENTIVE}`,
                {
                    etatIds:
                        this.filterStatus?.length === this.statusList?.length
                            ? []
                            : this.filterStatus.map(status => {
                                  switch (status) {
                                      case 'A_REALISER':
                                          return 1;
                                      case 'EN_COURS':
                                          return 2;
                                      case 'REALISE':
                                          return 3;
                                  }
                              }),
                    id: this.filterInterventionId !== '' ? this.filterInterventionId : undefined,
                    equipementIds: this.equipement !== null ? [this.equipement.id] : [],
                    dateDebut: this.filterPredictedDateFrom !== '' ? this.filterPredictedDateFrom : undefined,
                    dateFin: this.filterPredictedDateTo !== '' ? this.filterPredictedDateTo : undefined,
                }
            );
        },
    },
});
</script>

<style scoped>
.dataTable > div:first-child {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
</style>
